/**
 * FSRS+ Wave 2 — "My FSRS Tuning" card. Shows the student which subjects the
 * weekly optimization job has stretched (long intervals — they're acing it)
 * vs tightened (short intervals — they're struggling). Hides itself if no
 * tuning has run yet.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, TrendingUp, TrendingDown, Minus, BrainCircuit } from "lucide-react";

const BAND_META = {
  above:      { icon: TrendingUp,   tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "on-track": { icon: Minus,        tone: "text-slate-600 bg-slate-50 border-slate-200" },
  below:      { icon: TrendingDown, tone: "text-amber-700 bg-amber-50 border-amber-200" },
};

export default function FsrsTuningCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/student/fsrs-targets")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="fsrs-tuning-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your FSRS tuning…
        </div>
      </Card>
    );
  }
  if (!data || !data.targets || data.targets.length === 0) {
    return (
      <Card className="p-4 border-slate-200" data-testid="fsrs-tuning-empty">
        <h3 className="font-display font-bold text-sm flex items-center gap-2">
          <BrainCircuit className="w-4 h-4 text-blue-700" /> FSRS Tuning
        </h3>
        <p className="text-[12px] text-slate-500 mt-1.5">
          We need ~20 answers per subject before tuning kicks in. Keep reviewing and we'll
          start personalizing your intervals within a week.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 border-slate-200" data-testid="fsrs-tuning-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-bold text-sm flex items-center gap-2">
          <BrainCircuit className="w-4 h-4 text-blue-700" /> FSRS Tuning
          <span className="text-[10px] uppercase tracking-[0.12em] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
            {data.targets.length} subj
          </span>
        </h3>
        {data.last_updated && (
          <span className="text-[10px] text-slate-400">
            updated {new Date(data.last_updated).toLocaleDateString()}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Personalized retention targets based on your last 30 days of answers. Recomputed weekly.
      </p>
      <ul className="space-y-2">
        {data.targets.map((t) => {
          const meta = BAND_META[t.band] || BAND_META["on-track"];
          const Icon = meta.icon;
          return (
            <li
              key={t.subject_id}
              className={`flex items-start gap-3 p-2.5 rounded-md border ${meta.tone}`}
              data-testid={`fsrs-tuning-row-${t.subject_id}`}
            >
              <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold truncate">{t.subject_name}</span>
                  <span className="text-[11px] font-bold tabular-nums">{Math.round(t.target * 100)}%</span>
                </div>
                <p className="text-[11px] mt-0.5 leading-snug opacity-80">{t.explanation}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
